import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Slider, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Bar, Line, ComposedChart, ReferenceLine } from 'recharts';
import { NumericFormat } from 'react-number-format';
import { apiService } from '../../services/apiService';

// 2026-03-22: Combined Tax Drag chart — HC + RMD benefit bars stacked
// Data from backend endpoint, no local computation
const TaxDragComboChart = ({
  convAmt, setConvAmt, convTax, setConvTax,
  returnRate, setReturnRate, dragRate, setDragRate,
  currentAge, setCurrentAge, lifeYears, setLifeYears,
  distMethod, setDistMethod, yearsToStart, reinvestPct, distTaxRate
}) => {
  const [chartData, setChartData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const data = await apiService.getTaxDragChartData({
        conv_tax: convTax,
        conv_amt: convAmt,
        return_rate: returnRate / 100,
        drag_rate: dragRate / 100,
        method: distMethod,
        years_to_start: yearsToStart,
        life_years: lifeYears,
        current_age: currentAge,
        dist_reinvest_pct: reinvestPct,
        dist_tax_rate: distTaxRate || 0,
      });
      // Build cumulative benefits (nominal + PV) for both components
      const r = returnRate / 100;
      const baseYear = new Date().getFullYear();
      let cumulHc = 0, cumulRmd = 0;
      let cumulHcPv = 0, cumulRmdPv = 0;
      const year0 = {
        year: baseYear,
        age: currentAge,
        cumulHc: 0, cumulRmd: 0, cumulTotal: 0, cumulTotalPv: 0,
        hcDiff: 0, rmdDiff: 0,
      };
      const mapped = (data.years || []).map(row => {
          const hcDiff = row.hc_roth_dist - row.hc_tax_dist;
          const rmdDiff = row.rmd_roth_dist - row.rmd_tax_dist;
          const yearIdx = row.year - baseYear;
          const pvFactor = Math.pow(1 + r, yearIdx);
          cumulHc += hcDiff;
          cumulRmd += rmdDiff;
          cumulHcPv += hcDiff / pvFactor;
          cumulRmdPv += rmdDiff / pvFactor;
          return {
            year: row.year,
            age: row.age,
            cumulHc: Math.round(cumulHc),
            cumulRmd: Math.round(cumulRmd),
            cumulTotal: Math.round(cumulHc + cumulRmd),
            cumulTotalPv: Math.round(cumulHcPv + cumulRmdPv),
            hcDiff,
            rmdDiff,
          };
        });
      setChartData({
        years: [year0, ...mapped],
        hc_fv: data.hc_fv, hc_pv: data.hc_pv,
        rmd_fv: data.rmd_fv, rmd_pv: data.rmd_pv,
        total_fv: data.total_fv, total_pv: data.total_pv,
      });
    } catch (err) {
      console.error('Combo tax drag chart error:', err);
    }
  }, [convTax, convAmt, returnRate, dragRate, yearsToStart, lifeYears, currentAge, distMethod, reinvestPct, distTaxRate]);

  useEffect(() => {
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [fetchData]);

  if (!chartData || !chartData.years?.length) return null;

  return (
    <Box sx={{ mt: 0, mb: 0, border: '2px solid #b3e5fc', borderRadius: '8px', px: 0, pt: 0, pb: 1 }}>
      <Box sx={{ px: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0 }}>
        Tax-Drag Avoidance Benefits: (Hidden Roth Contribution + Reduced Taxable Distributions) Combined
      </Typography>

      {/* Inputs */}
      <Box display="flex" gap={2} flexWrap="nowrap" alignItems="center" sx={{ mb: 0 }}>
        <Box sx={{ minWidth: 145, maxWidth: 145 }}>
          <NumericFormat
            label="Conversion Amount"
            value={convAmt}
            onValueChange={(v) => setConvAmt(v.floatValue || 0)}
            thousandSeparator=","
            prefix="$"
            customInput={TextField}
            size="small"
            variant="outlined"
            fullWidth
            inputProps={{ style: { fontWeight: 'bold' } }}
          />
        </Box>
        <Box sx={{ minWidth: 145, maxWidth: 145 }}>
          <NumericFormat
            label="Outside Tax Payment"
            value={convTax}
            onValueChange={(v) => setConvTax(v.floatValue || 0)}
            thousandSeparator=","
            prefix="$"
            customInput={TextField}
            size="small"
            variant="outlined"
            fullWidth
            inputProps={{ style: { fontWeight: 'bold' } }}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Return: {returnRate.toFixed(1)}%</Typography>
          <Slider
            value={returnRate}
            onChange={(e, v) => setReturnRate(v)}
            min={1} max={15} step={0.5}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Tax Drag: {dragRate.toFixed(1)}%</Typography>
          <Slider
            value={dragRate}
            onChange={(e, v) => setDragRate(v)}
            min={0} max={15} step={0.5}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Life Years: {lifeYears}</Typography>
          <Slider
            value={lifeYears}
            onChange={(e, v) => setLifeYears(v)}
            min={10} max={35} step={1}
            size="small"
            valueLabelDisplay="auto"
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="caption">Dist Method</Typography>
          <ToggleButtonGroup
            value={distMethod}
            exclusive
            onChange={(e, v) => { if (v) setDistMethod(v); }}
            size="small"
          >
            <ToggleButton value="F" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>FA</ToggleButton>
            <ToggleButton value="R" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>RMD</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Terminal values */}
      <Box display="flex" gap={3} justifyContent="center" sx={{ mb: 0.5 }}>
        <Typography variant="body2">
          <strong>Hidden Contribution:</strong> <span style={{ color: '#1565c0' }}>${chartData.hc_fv?.toLocaleString()}</span> (PV: <strong>${chartData.hc_pv?.toLocaleString()}</strong>)
        </Typography>
        <Typography variant="body2">
          <strong>Taxable Distribution Reduction:</strong> <span style={{ color: '#2e7d32' }}>${chartData.rmd_fv?.toLocaleString()}</span> (PV: <strong>${chartData.rmd_pv?.toLocaleString()}</strong>)
        </Typography>
        <Typography variant="body2">
          <strong>Total Benefit:</strong> <span style={{ color: '#333' }}>${chartData.total_fv?.toLocaleString()}</span> (PV: <strong>${chartData.total_pv?.toLocaleString()}</strong>)
        </Typography>
      </Box>
      </Box>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData.years} margin={{ top: 5, right: 5, left: 5, bottom: -3 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis yAxisId="left" domain={[0, Math.ceil(((chartData.hc_pv || 0) + (chartData.rmd_pv || 0)) / 1000) * 1000]} tickCount={4} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v.toFixed(0)}`} label={{ value: 'Cumulative Benefit - PRESENT VALUE', angle: -90, position: 'center', dx: -22, style: { fontSize: '0.75rem', fill: '#333', fontWeight: 'bold' } }} />
          <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v.toFixed(0)}`} label={{ value: 'Cumulative Benefit - FUTURE VALUE', angle: 90, position: 'center', dx: 22, style: { fontSize: '0.75rem', fill: '#333', fontWeight: 'bold' } }} />
          <Tooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const row = payload[0]?.payload;
            if (!row) return null;
            return (
              <Box sx={{ backgroundColor: 'white', border: '1px solid #ccc', p: 1, fontSize: '0.875rem' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{label} (age {row.age})</Typography>
                <Box sx={{ color: '#1565c0', display: 'flex', gap: 1 }}><span style={{ minWidth: 55, textAlign: 'right' }}>${row.cumulHc?.toLocaleString()}</span> Hidden Contribution - Cum FV</Box>
                <Box sx={{ color: '#2e7d32', display: 'flex', gap: 1 }}><span style={{ minWidth: 55, textAlign: 'right' }}>${row.cumulRmd?.toLocaleString()}</span> Taxable Distribution Reduction - Cum FV</Box>
                <Box sx={{ color: '#333', fontWeight: 'bold', display: 'flex', gap: 1 }}><span style={{ minWidth: 55, textAlign: 'right' }}>${row.cumulTotal?.toLocaleString()}</span> Total Benefit - Cum FV</Box>
                <Box sx={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', gap: 1 }}><span style={{ minWidth: 55, textAlign: 'right' }}>${row.cumulTotalPv?.toLocaleString()}</span> Total Benefit - Cum PV</Box>
                <Box sx={{ color: '#888', mt: 0.5 }}>Year: Hidden Cont ${row.hcDiff?.toFixed(2)} | Tax Dist Redux ${row.rmdDiff?.toFixed(2)}</Box>
              </Box>
            );
          }} />
          <Legend height={25} />
          <Line yAxisId="left" type="monotone" dataKey="cumulTotalPv" name="Total Benefit PV" stroke="transparent" strokeWidth={0} dot={false} legendType="none" />
          <ReferenceLine yAxisId="left" y={chartData.hc_pv} stroke="transparent" label={({ viewBox }) => {
            const text = `\u2190 Hidden Contribution Present Value: $${chartData.hc_pv?.toLocaleString()}`;
            const x = viewBox.x + 8;
            return (<g><text x={x} y={viewBox.y + 4} textAnchor="start" fill="#1565c0" fontSize={11} fontWeight="bold">{text}</text></g>);
          }} />
          <ReferenceLine yAxisId="left" y={(chartData.hc_pv || 0) + (chartData.rmd_pv || 0)} stroke="transparent" label={({ viewBox }) => {
            const text = `\u2190 (Hidden Contribution + Reduced RMDs) Total Present Value: $${chartData.total_pv?.toLocaleString()}`;
            const x = viewBox.x + 8;
            return (<g><text x={x} y={viewBox.y + 4} textAnchor="start" fill="#2e7d32" fontSize={11} fontWeight="bold">{text}</text></g>);
          }} />
          <Bar yAxisId="right" dataKey="cumulHc" name="Hidden Contribution Benefit" stackId="1" fill="#90caf9" stroke="#1565c0" />
          <Bar yAxisId="right" dataKey="cumulRmd" name="Taxable Distribution Reduction Benefit" stackId="1" fill="#81c784" stroke="#2e7d32" />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TaxDragComboChart;
